import { useState, useEffect } from "react";
import { MessageCircleQuestion, ChevronDown, ChevronUp, Loader2, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

interface Question {
  id: string;
  question: string;
  author_name: string;
  answer: string | null;
  answered_at: string | null;
  helpful_count: number;
  created_at: string;
}

interface ProductQuestionsProps {
  productHandle: string;
  productId: string;
  productTitle: string;
}

export const ProductQuestions = ({ productHandle, productId, productTitle }: ProductQuestionsProps) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, [productHandle]);

  const fetchQuestions = async () => {
    try {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from('product_questions' as any)
        .select('*')
        .eq('product_handle', productHandle)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      setQuestions((data as any as Question[]) || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newQuestion.trim() || newQuestion.trim().length < 10) {
      toast.error("Question must be at least 10 characters");
      return;
    }

    setSubmitting(true);
    try {
      const supabase = await getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Sign in to ask questions");
        setSubmitting(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .maybeSingle();

      await supabase
        .from('product_questions' as any)
        .insert({
          user_id: user.id,
          product_handle: productHandle,
          product_id: productId,
          question: newQuestion.trim(),
          author_name: (profile as any)?.display_name || user.email?.split("@")[0] || "Customer",
        });

      setNewQuestion("");
      setShowForm(false);
      toast.success("Question submitted!", { description: "We'll answer it soon" });
      fetchQuestions();
    } catch {
      toast.error("Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleQuestions = showAll ? questions : questions.slice(0, 3);
  const timeAgo = (dateStr: string) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  return (
    <section className="border-t border-border py-10 md:py-14">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <MessageCircleQuestion className="h-5 w-5 text-foreground" />
            <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground">
              Questions & Answers
            </h2>
            {questions.length > 0 && (
              <span className="text-[13px] text-muted-foreground">({questions.length})</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="rounded-full text-[12px] uppercase tracking-wider"
          >
            Ask a Question
          </Button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-muted/30 p-4 border border-border space-y-3">
                <p className="text-[13px] font-medium text-foreground">
                  Ask about: {productTitle}
                </p>
                <textarea
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="What would you like to know about this product?"
                  className="w-full h-24 p-3 text-[13px] border border-border bg-background resize-none focus:outline-none focus:border-foreground transition-colors"
                  maxLength={500}
                />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{newQuestion.length}/500</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-[12px]">
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSubmit}
                      disabled={submitting || newQuestion.trim().length < 10}
                      className="rounded-full text-[12px] uppercase tracking-wider"
                    >
                      {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Submit"}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircleQuestion className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-[13px] text-muted-foreground">No questions yet. Be the first to ask!</p>
          </div>
        ) : (
          <div className="space-y-0 divide-y divide-border">
            {visibleQuestions.map((q) => (
              <div key={q.id} className="py-4">
                <button
                  onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                  className="w-full text-left flex items-start gap-3"
                >
                  <span className="text-[14px] font-bold text-primary mt-0.5">Q</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-foreground leading-snug">{q.question}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {q.author_name} · {timeAgo(q.created_at)}
                    </p>
                  </div>
                  {q.answer ? (
                    expanded === q.id ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <span className="text-[11px] text-muted-foreground italic flex-shrink-0">Awaiting answer</span>
                  )}
                </button>
                <AnimatePresence>
                  {expanded === q.id && q.answer && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-7 mt-2 pl-3 border-l-2 border-primary/20">
                        <span className="text-[14px] font-bold text-primary">A</span>
                        <p className="text-[13px] text-muted-foreground mt-0.5 leading-relaxed">{q.answer}</p>
                        {q.answered_at && (
                          <p className="text-[11px] text-muted-foreground mt-1">LoftLoom Team · {timeAgo(q.answered_at)}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}

        {questions.length > 3 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-4 text-[13px] font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors"
          >
            {showAll ? "Show fewer questions" : `View all ${questions.length} questions`}
          </button>
        )}
      </div>
    </section>
  );
};
